import { useEffect, useRef, useState } from 'react';

/**
 * La feuille qui monte du bas.
 *
 * Trois pièges d'iOS sont traités ici, et nulle part ailleurs :
 *
 *  1. Le défilement élastique du document continue SOUS la feuille : on ne le bloque
 *     pas avec `position:fixed` sur le body — qui fait sauter la page en haut et perd
 *     la position de lecture — mais en confinant le défilement au panneau
 *     (`overscroll-behavior: contain`) et en n'ayant jamais de document défilant.
 *  2. Le bas de l'écran appartient à l'indicateur home : sans
 *     `env(safe-area-inset-bottom)`, le dernier bouton de la feuille est sous le trait
 *     blanc et ne se touche pas.
 *  3. Le voile doit fermer la feuille au toucher. Il est pour cela un `button` et non
 *     un `div` : voir le commentaire à l'endroit même.
 */
export function Feuille({ ouverte, onFermer, titre, sousTitre, action, entete, hauteurMax = '85dvh', children }) {
  // Le glisser vers le bas pour refermer.
  //
  // Il n'existait pas : la feuille ne se fermait qu'au toucher du voile ou du bouton,
  // et le geste naturel — pousser la feuille vers le bas — ne faisait rien. Il est
  // capté sur la POIGNÉE et l'en-tête seulement, jamais sur la liste : autrement, un
  // défilement vers le haut en bas de liste refermerait la feuille au lieu de faire
  // rebondir le contenu.
  const [glisse, setGlisse] = useState(0);
  const depart = useRef(null);
  const panneau = useRef(null);

  useEffect(() => { if (!ouverte) { setGlisse(0); depart.current = null; } }, [ouverte]);

  // Le geste depuis le CORPS de la feuille, quand la liste est déjà en haut.
  //
  // C'est le geste réel sur téléphone : personne ne vise une poignée de cinq pixels
  // avec le pouce, on pousse la feuille là où on l'a sous le doigt. À la souris on
  // attrape l'en-tête, d'où un correctif qui marchait sur ordinateur et pas sur
  // téléphone. On ne prend la main que si le défilement est à zéro — sinon un balayage
  // vers le bas doit faire ce qu'il a toujours fait : remonter la liste.
  const depuisContenu = {
    onPointerDown: (e) => {
      if ((panneau.current?.scrollTop ?? 0) > 0) return;
      if (e.target instanceof Element && e.target.closest('button, input, textarea, a')) return;
      depart.current = e.clientY;
    },
    onPointerMove: (e) => {
      if (depart.current == null) return;
      const d = e.clientY - depart.current;
      if (d <= 0) { setGlisse(0); return; }
      setGlisse(d);
    },
    onPointerUp: (e) => {
      if (depart.current == null) return;
      const parcouru = e.clientY - depart.current;
      depart.current = null;
      setGlisse(0);
      if (parcouru > 90) onFermer?.();
    },
    onPointerCancel: () => { depart.current = null; setGlisse(0); },
  };

  const prise = {
    // `none` et non `manipulation` : ici le geste vertical nous appartient, et laisser
    // iOS l'interpréter comme un défilement le ferait annuler en cours de route.
    style: { touchAction: 'none' },
    onPointerDown: (e) => {
      // Un geste qui commence SUR un bouton n'est pas un geste : c'est un appui.
      // Sans cette garde, la capture du pointeur posée pour suivre le glissement
      // avalait le clic, et « Fermer » ne répondait plus.
      if (e.target instanceof Element && e.target.closest('button')) return;
      depart.current = e.clientY;
      e.currentTarget.setPointerCapture?.(e.pointerId);
    },
    onPointerMove: (e) => {
      if (depart.current == null) return;
      // Vers le bas seulement : tirer vers le haut ne doit pas décoller la feuille.
      setGlisse(Math.max(0, e.clientY - depart.current));
    },
    onPointerUp: (e) => {
      if (depart.current == null) return;
      const parcouru = e.clientY - depart.current;
      depart.current = null;
      setGlisse(0);
      // 90 px : assez pour qu'un tremblement de pouce ne referme rien, assez peu pour
      // que le geste aboutisse sans traverser tout l'écran.
      if (parcouru > 90) onFermer?.();
    },
    // Un appel entrant pendant le geste laisse sinon la feuille à mi-course.
    onPointerCancel: () => { depart.current = null; setGlisse(0); },
  };

  useEffect(() => {
    if (!ouverte) return;
    const auClavier = (e) => { if (e.key === 'Escape') onFermer?.(); };
    document.addEventListener('keydown', auClavier);
    return () => document.removeEventListener('keydown', auClavier);
  }, [ouverte, onFermer]);

  if (!ouverte) return null;

  return (
    <>
      {/* Le voile est un VRAI bouton, et c'est le correctif.
          Il était un `div` avec des gestionnaires `pointerdown`/`pointerup` : sur iOS,
          un toucher sur un élément non interactif peut être classé comme un début de
          défilement, auquel cas Safari envoie `pointercancel` et jamais `pointerup` —
          la feuille ne se fermait pas, et le toucher suivant repartait sur le terrain.
          Un `button` reçoit un `click` fiable, qui n'est de toute façon émis que si le
          doigt se lève au même endroit : le garde anti-glissement devient inutile.

          Le flou d'arrière-plan a été retiré de cette couche : `backdrop-filter` crée
          un contexte d'empilement et WebKit a une histoire de tests de survol fautifs
          dessus. Un voile plus dense fait le même travail sans le risque. */}
      <button
        type="button"
        aria-label="Fermer"
        onClick={onFermer}
        style={{
          position: 'absolute', inset: 0, zIndex: 60,
          background: 'rgba(0,0,0,.62)',
          // `cursor: pointer` n'est pas décoratif sur iOS : c'est l'indice historique
          // qui rend les événements de clic fiables sur cette plateforme.
          cursor: 'pointer',
          touchAction: 'manipulation',
          border: 0, padding: 0, display: 'block', width: '100%',
        }}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={titre}
        style={{
          // 61 : AU-DESSUS de la bannière de mise à jour, qui est à 50. Le voile était
          // à 40 et la bannière flottait donc par-dessus la feuille ouverte.
          position: 'absolute', left: 0, right: 0, bottom: 0, zIndex: 61,
          // En `dvh` et non en pourcentage : un pourcentage se résout contre le bloc
          // conteneur, donc contre toute la chaîne d'ancêtres positionnés. Quand cette
          // hauteur n'est pas définie de façon ferme, la boîte flexible ne contraint
          // plus ses enfants, le contenu déborde au lieu de défiler — et la feuille
          // devient impossible à parcourir. `dvh` ne dépend de rien d'autre que de
          // l'écran, et suit la barre d'URL d'iOS quand elle se rétracte.
          maxHeight: hauteurMax, display: 'flex', flexDirection: 'column',
          transform: glisse ? `translateY(${glisse}px)` : 'none',
          transition: glisse ? 'none' : 'transform .22s cubic-bezier(.4,0,.2,1)',
          background: 'var(--surf)', borderTop: '1px solid var(--line)',
          borderRadius: '22px 22px 0 0', boxShadow: 'var(--ombre)',
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}
      >
        <div {...prise} style={{ ...prise.style, padding: '10px 0 6px', display: 'flex', justifyContent: 'center', flex: 'none', cursor: 'grab' }}>
          <div style={{ width: 42, height: 5, borderRadius: 3, background: 'var(--line)' }} />
        </div>

        {(titre || action) && (
          <div {...prise} style={{ ...prise.style, padding: '6px 18px 10px', display: 'flex', alignItems: 'center', gap: 12, flex: 'none' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              {titre && <div style={{ fontSize: 17, fontWeight: 800, letterSpacing: '-.2px' }}>{titre}</div>}
              {sousTitre && <div style={{ fontSize: 11.5, fontFamily: 'var(--chiffres)', color: 'var(--ink2)', marginTop: 3 }}>{sousTitre}</div>}
            </div>
            {action}
            <button onClick={onFermer} aria-label="Fermer" style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink2)', padding: '10px 4px' }}>Fermer</button>
          </div>
        )}

        {/* L'en-tête épinglé : la recherche ne doit pas s'en aller en défilant, c'est
            elle qu'on vise quand on connaît le nom et pas le poste. */}
        {entete && <div style={{ flex: 'none' }}>{entete}</div>}

        {/* `flex: 1 1 0%` + `minHeight: 0` : sans la base nulle, l'enfant réclame la
            hauteur de son contenu et refuse de rétrécir. Le débordement est déclaré
            ici en ligne plutôt que laissé à la classe, et `touchAction: pan-y` dit à
            iOS que le geste vertical appartient à cette boîte. */}
        <div
          ref={panneau}
          className="defile"
          {...depuisContenu}
          style={{
            flex: '1 1 0%', minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch',
            // `none` et non `contain` : le rebond élastique d'iOS, en haut de liste,
            // avale le geste vers le bas et l'application ne voit plus passer que des
            // `pointercancel`. Sans rebond, le geste nous revient.
            overscrollBehavior: 'none',
            touchAction: 'pan-y',
          }}
        >
          {children}
        </div>
      </div>
    </>
  );
}
